import { useEffect, useRef, useState } from "react";

import {
  SpecialistAboutTab,
  SpecialistCalendarTab,
  SpecialistProfileHero,
  SpecialistProfileHeroSkeleton,
  SpecialistReviewsTab,
  localisedPath,
} from "@/components";
import routes from "@/constants/routes";
import { useGetSpecialistQuery } from "@/store/apis";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Button, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui";

import { ApiError, formatErrorMessage } from "@/lib/utils";

function SpecialistIframePage() {
  const { t, i18n } = useTranslation();
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const tabsContentRef = useRef<HTMLDivElement>(null);

  const getTabHash = () => {
    return location.hash.substring(1);
  };

  const [activeTab, setActiveTab] = useState<string>(getTabHash() || "calendar");

  useEffect(() => {
    const handleHashChange = () => {
      setActiveTab(getTabHash());
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [location.hash]);

  const { data: specialist, isLoading, error } = useGetSpecialistQuery({ id: slug?.toString(), lang: i18n.language });

  const onTabChange = (tab: string) => {
    setActiveTab(tab);
    const navigateTo = routes.specialistIframe(slug, tab);
    navigate(localisedPath(navigateTo));
    if (tab === "calendar" && tabsContentRef.current) {
      tabsContentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const specialist_name = `${specialist?.first_name} ${specialist?.last_name}`;

  const triggerClassName =
    "rounded-none px-0 text-center text-sm font-medium text-primaryText hover:text-secondaryText data-[state=active]:border-b-2 data-[state=active]:border-primaryApp data-[state=active]:bg-inherit data-[state=active]:text-primaryText/90 data-[state=active]:shadow-none lg:basis-1/6 lg:pb-[15px] lg:text-secondaryText";
  const tabClassName = "w-full self-stretch lg:rounded-lg lg:py-6";

  return (
    <div className="min-h-screen py-10">
      <div className="w-full flex-none justify-center space-y-2 overflow-auto pt-5 lg:flex lg:flex-row lg:justify-start lg:space-x-8 lg:px-0">
        <div>
          <div className="space-y-2 pt-5 lg:pt-2">
            {isLoading && <SpecialistProfileHeroSkeleton />}
            {error && formatErrorMessage(error as ApiError)}
            {specialist && (
              <SpecialistProfileHero
                specialist_name={specialist_name}
                main_photo={specialist.main_photo}
                specialisations={specialist.specialisations}
                average_rating={specialist.average_rating}
                reviews_count={specialist.reviews_count}
              />
            )}
            <div className="text-center">
              {isLoading && (
                <Skeleton className={`${activeTab === "calendar" ? "lg:hidden" : ""} mx-auto h-10 w-full`} />
              )}
              {specialist && (
                <Button
                  onClick={() => onTabChange("calendar")}
                  variant="yellow"
                  className={`${activeTab === "calendar" ? "lg:hidden" : ""} mx-auto w-full grow rounded-none border-none bg-primaryIframe text-primaryText lg:font-semibold`}
                >
                  {t("specialistProfilePage.scheduleSession")}
                </Button>
              )}
            </div>
          </div>
        </div>
        {isLoading && <Skeleton className="hidden lg:block lg:h-[50px] lg:w-full lg:rounded-lg lg:border lg:pt-1.5" />}
        {specialist && (
          <Tabs
            key={activeTab}
            defaultValue={activeTab}
            className="w-full space-y-4 overflow-hidden pt-5 lg:space-y-0 lg:pt-0"
            onValueChange={onTabChange}
          >
            <TabsList className="inline-flex items-center justify-start gap-5 bg-inherit p-0 lg:h-[50px] lg:w-full lg:justify-center lg:rounded-lg lg:border lg:bg-white lg:px-8 lg:pt-1.5">
              <TabsTrigger id="calendar" value="calendar" className={triggerClassName}>
                {t("specialistProfilePage.calendar")}
              </TabsTrigger>
              <TabsTrigger id="about" value="about" className={triggerClassName}>
                {t("specialistProfilePage.about")}
              </TabsTrigger>
              <TabsTrigger id="reviews" value="reviews" className={triggerClassName}>
                {t("specialistProfilePage.reviews")}
              </TabsTrigger>
              <div className="hidden lg:basis-1/2" />
            </TabsList>
            <TabsContent ref={tabsContentRef} className={tabClassName} value="calendar">
              <SpecialistCalendarTab specialist={specialist} iframe={true} />
            </TabsContent>
            <TabsContent className={tabClassName} value="about">
              <SpecialistAboutTab specialist={specialist} />
            </TabsContent>
            <TabsContent className={tabClassName} value="reviews">
              <SpecialistReviewsTab reviews={specialist.reviews} reviews_count={specialist.reviews_count} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

export { SpecialistIframePage };
